import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../services/useAuth";
import { translate } from "../../../hooks/translate";
import { useTitle } from "../../../hooks/useTitle";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import { buildRadioFilterSection } from "../shared/buildListFilterSections";
import { useFormatValue } from "../../../hooks/useFormatValue";
import { useModalConfirm as confirm } from "../../../hooks/useModalConfirm";
import { withAuth } from "../../../components/HOC/withAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import {
  formatDateTimeTrInAppTz,
  formatInstantDateShortTr,
} from "../../../utils/appTime";
import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import Button from "../../../components/BaseUI/Button";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import Input from "../../../components/BaseUI/Input";
import Label from "../../../components/BaseUI/Label";
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
} from "../../../components/BaseUI/Dropdown";
import Lozenge from "../../../components/BaseUI/Lozenge";
import {
  Modal,
  ModalHeader,
  ModalBody,
} from "../../../components/BaseUI/Modal";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  FiPackage,
  FiPlus,
  FiEye,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiChevronDown,
  FiChevronUp,
  FiFileText,
} from "react-icons/fi";
import { generateDeliveryProductsPDF } from "../../../utils/generateOfferPDF";
import PdfPreviewModal from "../../../components/CustomUI/PdfPreviewModal";
import { usePdfPreview } from "../../../hooks/usePdfPreview";
import "./StockTransfers.scss";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const StockTransfers = () => {
  useTitle("Rovion MES - Sevkiyatlar");
  const { getToken, getUser } = useAuth();
  const navigate = useNavigate();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/deliveries",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );

  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterSale, setFilterSale] = useState(null);
  const [filterCompany, setFilterCompany] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [modelsModalOpen, setModelsModalOpen] = useState(false);
  const [selectedTransferForModels, setSelectedTransferForModels] =
    useState(null);
  const {
    pdfPreview,
    closePdfPreview,
    openPdfPreview,
    downloadPdfFromPreview,
  } = usePdfPreview();

  const format = useFormatValue;

  const deliveryDateSortMs = (transfer) => {
    if (!transfer) return 0;
    if (transfer.deliveryDate) {
      const d = new Date(transfer.deliveryDate);
      if (!Number.isFinite(d.getTime())) return 0;
      if (transfer.deliveryTime && typeof transfer.deliveryTime === "string") {
        const parts = transfer.deliveryTime.trim().split(":");
        const h = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10);
        if (Number.isFinite(h) && Number.isFinite(m)) {
          d.setHours(h, m, 0, 0);
        }
      }
      return d.getTime();
    }
    const t = new Date(transfer.transferDate || transfer.createdAt).getTime();
    return Number.isFinite(t) ? t : 0;
  };

  const headers = [
    {
      name: "Sevkiyat Adı",
      dataType: "text",
      id: 0,
      customSorter: (a, b) =>
        String(a.deliveryName || "").localeCompare(
          String(b.deliveryName || ""),
          undefined,
          { numeric: true, sensitivity: "base" },
        ),
    },
    {
      name: "Sipariş",
      dataType: "text",
      id: 1,
      customSorter: (a, b) =>
        String(a.saleId?.saleName || "").localeCompare(
          String(b.saleId?.saleName || ""),
          undefined,
          { numeric: true, sensitivity: "base" },
        ),
    },
    {
      name: "Firma",
      dataType: "text",
      id: 2,
      customSorter: (a, b) =>
        String(a.saleId?.companyId?.name || "").localeCompare(
          String(b.saleId?.companyId?.name || ""),
          undefined,
          { numeric: true, sensitivity: "base" },
        ),
    },
    {
      name: "Teslim Edilen",
      dataType: "number",
      id: 3,
      customSorter: (a, b) =>
        (a.devices || []).length - (b.devices || []).length,
    },
    {
      name: "Sevkiyat Tarihi",
      dataType: "date",
      id: 4,
      customSorter: (a, b) => deliveryDateSortMs(a) - deliveryDateSortMs(b),
    },
    {
      name: "Teslim Alan Adı",
      dataType: "text",
      id: 5,
      customSorter: (a, b) =>
        String(a.driverName || "").localeCompare(
          String(b.driverName || ""),
          undefined,
          { numeric: true, sensitivity: "base" },
        ),
    },
    {
      name: "Araç Plakası",
      dataType: "text",
      id: 6,
      customSorter: (a, b) =>
        String(a.vehiclePlate || "").localeCompare(
          String(b.vehiclePlate || ""),
          undefined,
          { numeric: true, sensitivity: "base" },
        ),
    },
    { name: "İşlemler", id: 7, sortable: false },
  ];

  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("deliveries-list", headers);

  const addToast = (message, type = "info") => {
    const newToast = {
      id: Date.now(),
      message,
      type,
    };
    setToasts([...toasts, newToast]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== newToast.id));
    }, 3000);
  };

  useEffect(() => {
    fetchTransfers();
  }, []);

  const fetchTransfers = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/deliveries`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const transfersData = response.data?.deliveries || response.data;
      setTransfers(Array.isArray(transfersData) ? transfersData : []);
    } catch (error) {
      console.error("Failed to fetch deliveries:", error);
      addToast("Sevkiyatlar yüklenemedi", "danger");
      setTransfers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (transferId) => {
    confirm({
      title: translate("UI.DELETE_DELIVERY") || "Sevkiyatı Sil",
      message:
        translate("UI.DELETE_DELIVERY_CONFIRM") ||
        "Bu sevkiyatı silmek istediğinizden emin misiniz?",
      appearance: "danger",
      confirmButtonText: translate("UI.DELETE") || "Sil",
      cancelButtonText: translate("UI.CANCEL") || "İptal",
      onConfirm: async () => {
        try {
          const token = getToken();
          await axios.delete(`${API_URL}/api/deliveries/${transferId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          addToast("Sevkiyat başarıyla silindi", "success");
          fetchTransfers();
        } catch (error) {
          console.error("Failed to delete delivery:", error);
          addToast(
            error.response?.data?.message || "Sevkiyat silinemedi",
            "danger",
          );
        }
      },
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    return formatDateTimeTrInAppTz(dateString);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return formatInstantDateShortTr(dateString);
  };

  // Helper function to limit text to 3 words
  const limitToThreeWords = (text) => {
    if (!text) return "-";
    const words = text.trim().split(/\s+/);
    return words.slice(0, 3).join(" ");
  };

  const handleViewModels = (transfer) => {
    setSelectedTransferForModels(transfer);
    setModelsModalOpen(true);
  };

  const handlePreviewDeliveryPdf = async (transfer) => {
    if (!transfer?._id) return;
    const deliveryName =
      transfer.deliveryName ||
      transfer.saleId?.saleName ||
      `Sevkiyat #${String(transfer._id).slice(-6)}`;
    openPdfPreview({
      title: `Sevkiyat PDF — ${deliveryName}`,
      generate: async (currencyOptions) => {
        let delivery = transfer;
        if (!transfer.createdBy?.name || !(transfer.devices || []).length) {
          const token = getToken();
          const response = await axios.get(
            `${API_URL}/api/deliveries/${transfer._id}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          delivery = response.data?.delivery || response.data;
        }
        return generateDeliveryProductsPDF(delivery, {
          action: "blob",
          ...currencyOptions,
        });
      },
    });
  };

  const uniqueSales = [
    ...new Set(
      (transfers || []).map((t) => t.saleId?.saleName).filter(Boolean),
    ),
  ];

  const uniqueCompanies = [
    ...new Set(
      (transfers || []).map((t) => t.saleId?.companyId?.name).filter(Boolean),
    ),
  ];

  const filteredTransfers = (transfers || []).filter((transfer) => {
    const query = searchQuery.toLowerCase();
    const matchesSearch =
      !query ||
      transfer?.saleId?.saleName?.toLowerCase().includes(query) ||
      transfer?.deliveryName?.toLowerCase().includes(query) ||
      transfer?.fromCompanyId?.name?.toLowerCase().includes(query) ||
      transfer?.toCompanyId?.name?.toLowerCase().includes(query) ||
      transfer?.fromDealerId?.name?.toLowerCase().includes(query) ||
      transfer?.toDealerId?.name?.toLowerCase().includes(query) ||
      transfer?.notes?.toLowerCase().includes(query);
    if (!matchesSearch) return false;
    if (filterSale && transfer?.saleId?.saleName !== filterSale) return false;
    if (filterCompany && transfer?.saleId?.companyId?.name !== filterCompany)
      return false;
    return true;
  });

  const activeFiltersCount = [filterSale, filterCompany].filter(Boolean).length;

  const clearDeliveryFilters = useCallback(() => {
    setFilterSale(null);
    setFilterCompany(null);
  }, []);

  const deliveryFilterSections = useMemo(
    () => [
      buildRadioFilterSection({
        id: "sale",
        title: "Sipariş Filtresi",
        value: filterSale || "",
        onChange: (v) => setFilterSale(v ? String(v) : null),
        options: [
          { value: "", label: "Tüm Siparişler" },
          ...uniqueSales.map((s) => ({ value: s, label: s })),
        ],
      }),
      buildRadioFilterSection({
        id: "company",
        title: "Firma Filtresi",
        value: filterCompany || "",
        onChange: (v) => setFilterCompany(v ? String(v) : null),
        options: [
          { value: "", label: "Tüm Cariler" },
          ...uniqueCompanies.map((c) => ({ value: c, label: c })),
        ],
      }),
    ],
    [filterSale, filterCompany, uniqueSales, uniqueCompanies],
  );

  const viewOptionsPanel = (
    <ListViewOptionsPanel
      columns={headers}
      visibleColumns={visibleColumns}
      onToggleColumn={toggleColumnVisibility}
      onResetColumns={resetToDefault}
      filterSections={deliveryFilterSections}
      activeFilterCount={activeFiltersCount}
      onReset={clearDeliveryFilters}
      showApply={false}
    />
  );

  return (
    <AppPage
      title="Sevkiyatlar"
      actions={
        canWrite ? (
          <Button
            appearance="primary-animated"
            onClick={() => navigate("/deliveries/create")}
            icon={<FiPlus />}
          >
            Yeni Sevkiyat
          </Button>
        ) : undefined
      }
    >
      <CardContainer>
        {loading ? (
          <div className="loading-container">
            <FiPackage className="spin" size={48} />
            <p>Yükleniyor...</p>
          </div>
        ) : (
          <>
            <Table
              columns={headers}
              visibleColumns={visibleColumns}
              tableId="deliveries-list"
              checkboxSelection
              loading={loading}
              viewOptions={viewOptionsPanel}
            >
              {Array.isArray(filteredTransfers) &&
                filteredTransfers.map((transfer) => {
                  // Group devices by model to show counts
                  const modelCounts = {};
                  const totalDevices = (transfer.devices || []).length;
                  (transfer.devices || []).forEach((device) => {
                    const modelName =
                      device?.modelId?.name ||
                      device?.model?.name ||
                      "Bilinmeyen";
                    const modelId =
                      device?.modelId?._id ||
                      device?.modelId ||
                      device?.model?._id;
                    if (!modelCounts[modelName]) {
                      modelCounts[modelName] = {
                        count: 0,
                        modelId: modelId,
                        modelCode:
                          device?.modelId?.code || device?.model?.code || "",
                        modelPrice:
                          device?.modelId?.price || device?.model?.price || 0,
                      };
                    }
                    modelCounts[modelName].count += 1;
                  });

                  return (
                    <Tr key={transfer._id} id={transfer._id} data={transfer}>
                      {visibleColumns.includes(0) && (
                        <Td>
                          <strong>
                            {limitToThreeWords(transfer.deliveryName)}
                          </strong>
                        </Td>
                      )}
                      {visibleColumns.includes(1) && (
                        <Td>
                          <strong>
                            {limitToThreeWords(transfer.saleId?.saleName)}
                          </strong>
                        </Td>
                      )}
                      {visibleColumns.includes(2) && (
                        <Td>
                          <strong>
                            {limitToThreeWords(
                              transfer.saleId?.companyId?.name || "-",
                            )}
                          </strong>
                        </Td>
                      )}
                      {visibleColumns.includes(3) && (
                        <Td>
                          <div className="d-flex align-items-center gap-2">
                            <FiPackage className="text-info" />
                            <Lozenge
                              appearance="info-subtle"
                              style={{ cursor: "pointer" }}
                              onClick={() => handleViewModels(transfer)}
                              title={
                                translate("UI.VIEW_MODELS") || "View Models"
                              }
                            >
                              {totalDevices}
                            </Lozenge>
                          </div>
                        </Td>
                      )}
                      {visibleColumns.includes(4) && (
                        <Td>
                          {transfer.deliveryDate
                            ? `${formatDate(transfer.deliveryDate)} ${transfer.deliveryTime || ""}`.trim()
                            : formatDateTime(
                                transfer.transferDate || transfer.createdAt,
                              )}
                        </Td>
                      )}
                      {visibleColumns.includes(5) && (
                        <Td>{transfer.driverName || "-"}</Td>
                      )}
                      {visibleColumns.includes(6) && (
                        <Td>{transfer.vehiclePlate || "-"}</Td>
                      )}
                      {visibleColumns.includes(7) && (
                        <Td>
                          <div className="d-flex align-items-center gap-1">
                            <IconButton
                              icon={<FiEye style={{ fontSize: "18px" }} />}
                              appearance="subtle"
                              shape="circle"
                              onClick={() =>
                                navigate(`/deliveries/view/${transfer._id}`)
                              }
                              title="Görüntüle"
                            />
                            <IconButton
                              icon={<FiFileText style={{ fontSize: "18px" }} />}
                              appearance="subtle"
                              shape="circle"
                              onClick={() => handlePreviewDeliveryPdf(transfer)}
                              title="PDF Önizle"
                            />
                            {canWrite && (
                              <>
                                <IconButton
                                  icon={
                                    <FiEdit2 style={{ fontSize: "18px" }} />
                                  }
                                  appearance="subtle"
                                  shape="circle"
                                  onClick={() =>
                                    navigate(`/deliveries/edit/${transfer._id}`)
                                  }
                                  title="Düzenle"
                                />
                                <IconButton
                                  icon={
                                    <FiTrash2 style={{ fontSize: "18px" }} />
                                  }
                                  appearance="subtle"
                                  shape="circle"
                                  onClick={() => handleDelete(transfer._id)}
                                  title="Sil"
                                />
                              </>
                            )}
                          </div>
                        </Td>
                      )}
                    </Tr>
                  );
                })}
            </Table>

            {(!filteredTransfers || filteredTransfers.length === 0) && (
              <div className="empty-state">
                <FiPackage size={64} />
                <p>Sevkiyat bulunamadı</p>
              </div>
            )}
          </>
        )}
      </CardContainer>

      <ToastContainer>
        {(toasts || []).map((toast) => (
          <Toast
            key={toast.id}
            title={toast.message}
            appearance={toast.type}
            onDismissed={() =>
              setToasts((toasts || []).filter((t) => t.id !== toast.id))
            }
          />
        ))}
      </ToastContainer>

      <Modal isOpen={modelsModalOpen} onClose={() => setModelsModalOpen(false)}>
        <ModalHeader>
          {translate("UI.DELIVERY_MODELS") || "Teslim Edilen Modeller"}
          {selectedTransferForModels?.saleId?.saleName && (
            <span className="text-muted small ms-2">
              - {limitToThreeWords(selectedTransferForModels.saleId.saleName)}
            </span>
          )}
        </ModalHeader>
        <ModalBody>
          {selectedTransferForModels?.devices &&
          selectedTransferForModels.devices.length > 0 ? (
            <div>
              {(() => {
                // Group devices by model
                const modelGroups = {};
                (selectedTransferForModels.devices || []).forEach((device) => {
                  const modelName =
                    device?.modelId?.name ||
                    device?.model?.name ||
                    translate("UI.UNKNOWN_MODEL");
                  const modelId =
                    device?.modelId?._id ||
                    device?.modelId ||
                    device?.model?._id;

                  if (!modelGroups[modelName]) {
                    modelGroups[modelName] = {
                      modelId: modelId,
                      modelName: modelName,
                      count: 0,
                    };
                  }
                  modelGroups[modelName].count += 1;
                });

                const totalDevices =
                  selectedTransferForModels.devices.length || 0;
                const totalModels = Object.keys(modelGroups).length;

                return (
                  <>
                    <div className="models-summary-simple mb-3">
                      <div className="summary-item">
                        <span className="summary-label">
                          {translate("UI.TOTAL_MODELS") || "Total Models"}:
                        </span>
                        <span className="summary-value">{totalModels}</span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-label">
                          {translate("UI.TOTAL_DEVICES") || "Total Devices"}:
                        </span>
                        <span className="summary-value text-success">
                          {totalDevices}
                        </span>
                      </div>
                    </div>
                    {Object.values(modelGroups).map((modelGroup, index) => (
                      <div key={index} className="model-item-simple">
                        <div className="model-item-header">
                          <div className="model-name-simple">
                            {modelGroup.modelName ||
                              translate("UI.UNKNOWN_MODEL")}
                          </div>
                          <Lozenge appearance="success-subtle">
                            {modelGroup.count || 0}
                          </Lozenge>
                        </div>
                      </div>
                    ))}
                  </>
                );
              })()}
            </div>
          ) : (
            <div className="empty-models-state">
              <FiPackage size={32} className="empty-icon" />
              <div className="empty-text">
                {translate("UI.NO_DEVICES_FOUND") ||
                  "No devices found in this delivery"}
              </div>
            </div>
          )}
        </ModalBody>
      </Modal>

      <PdfPreviewModal
        isOpen={pdfPreview.open}
        onClose={closePdfPreview}
        title={pdfPreview.title}
        previewUrl={pdfPreview.url}
        fileName={pdfPreview.fileName}
        loading={pdfPreview.loading}
        error={pdfPreview.error}
        onDownload={downloadPdfFromPreview}
      />
    </AppPage>
  );
};

export default withAuth(StockTransfers, ["admin", "employee", "user"]);
